import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { IconCar, IconChevronRight } from '@tabler/icons-react';
import { useRidesAvailabilityService, isRidesAvailabilityLive, isRidesBookingActive, RIDES_AVAILABILITY_STATUS, type RidesAvailabilityView } from '@actschurch/ccms-sdk';
import { ridesKeys } from '@/lib/queryKeys';
import { formatShortDate, formatTime, isPastDate } from '@/lib/format';
import type { PillKind } from '@/lib/copy';
import { useCopy } from '@/lib/settings';
import { StatusPill } from '@/components/ui/StatusPill';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingState } from '@/components/ui/LoadingState';
import { Callout } from '@/components/ui/Callout';
import { DateTile } from '@/components/rides/EventPicker';
import { useDriverReadiness } from '@/components/flows/DriverReadiness';
import { SuccessBurst } from '@/components/flows/RideTicket';
import '@/styles/flows.css';

/** Status words for a lift (never the raw status name). */
export function listingPill(l: RidesAvailabilityView): { label: string; kind: PillKind | 'neutral' } {
  switch (l.statusID) {
    case RIDES_AVAILABILITY_STATUS.Open: return { label: l.seatsOpen === 1 ? '1 seat free' : `${l.seatsOpen} seats free`, kind: 'ok' };
    case RIDES_AVAILABILITY_STATUS.Full: return { label: 'Full', kind: 'info' };
    case RIDES_AVAILABILITY_STATUS.Closed: return { label: 'Not taking bookings', kind: 'info' };
    case RIDES_AVAILABILITY_STATUS.Arrived: return { label: "You've arrived", kind: 'ok' };
    case RIDES_AVAILABILITY_STATUS.Cancelled: return { label: 'Cancelled', kind: 'bad' };
    case RIDES_AVAILABILITY_STATUS.Completed: return { label: 'Done', kind: 'neutral' };
    default: return { label: 'See details', kind: 'info' };
  }
}

/** Names of the riders still booked (a child's own name on an under-18 booking). */
export function riderNames(l: RidesAvailabilityView): string[] {
  return (l.bookings ?? [])
    .filter((b) => isRidesBookingActive(b.statusID))
    .map((b) => (b.forMinor && b.childName?.trim() ? b.childName.trim() : b.riderName))
    .filter(Boolean);
}

/** My lifts: what's coming up first, then past and cancelled lifts. */
export default function DriveListingsPage() {
  const [params] = useSearchParams();
  const { t } = useCopy();
  const availability = useRidesAvailabilityService();
  const readiness = useDriverReadiness();
  const q = useQuery({ queryKey: ridesKeys.availability.mine, queryFn: () => availability.getMine(), staleTime: 30_000 });
  const live = (q.data ?? []).filter((l) => !isPastDate(l.date) && isRidesAvailabilityLive(l.statusID)).sort((a, b) => a.eventDate - b.eventDate || (a.departureTime ?? '').localeCompare(b.departureTime ?? ''));
  const rest = (q.data ?? []).filter((l) => !live.includes(l)).sort((a, b) => b.eventDate - a.eventDate);
  const justListed = !!params.get('listed');

  return (
    <div className="rides-main rides-flow">
      {justListed ? (
        <div className="rides-celebrate" aria-live="polite">
          <SuccessBurst label="Lift offered" />
          <h1 className="rides-title">Your lift is ready</h1>
          <p className="rides-lede">{t('drive.listed_ready')}</p>
        </div>
      ) : (
        <header className="rides-stephead rides-edithead" style={{ paddingTop: 16 }}>
          <h1 className="rides-title">My lifts</h1>
          <p className="rides-lede">{t('drive.lifts_intro')}</p>
        </header>
      )}

      {q.isLoading && <LoadingState label="Loading your lifts" count={3} />}
      {q.error && <ErrorState title="We couldn't load your lifts" error={q.error} onRetry={() => q.refetch()} retrying={q.isFetching} />}

      {q.data && (
        <div className="rides-flow-stack">
          {!readiness.loading && !readiness.isNew && readiness.overall !== 'ready' && (
            <Callout
              tone={readiness.overall === 'waiting' ? 'info' : 'warn'}
              title={readiness.overall === 'waiting' ? 'Your checks are not finished yet' : 'Your checklist is not finished yet'}
              action={<Button variant="default" component={Link} to="/drive/profile">See your checklist</Button>}
            >
              {readiness.overall === 'waiting' ? t('drive.ready_waiting') : t('drive.ready_todo')}
            </Callout>
          )}

          {live.length === 0 && rest.length === 0 && (
            <EmptyState icon={<IconCar size={28} />} title="No lifts yet" action={<Button component={Link} to="/drive/events" fullWidth>Offer a lift</Button>}>
              {t('drive.lifts_empty')}
            </EmptyState>
          )}

          {live.length > 0 && (
            <section>
              <h2 className="rides-sectlabel" style={{ marginTop: 0 }}>Coming up</h2>
              <div className="rides-stack">{live.map((l) => <LiftRow key={l.rideAvailabilityID} l={l} />)}</div>
            </section>
          )}
          {rest.length > 0 && (
            <section>
              <h2 className="rides-sectlabel">Past and cancelled</h2>
              <div className="rides-stack">{rest.slice(0, 20).map((l) => <LiftRow key={l.rideAvailabilityID} l={l} />)}</div>
            </section>
          )}

          {(live.length > 0 || rest.length > 0) && (
            <Button variant="default" fullWidth component={Link} to="/drive/events" leftSection={<IconCar size={20} aria-hidden="true" />}>Offer another lift</Button>
          )}
        </div>
      )}
    </div>
  );
}

function LiftRow({ l }: { l: RidesAvailabilityView }) {
  const pill = listingPill(l);
  const riders = riderNames(l);
  const time = l.departureTime ? `Leaves ${formatTime(l.departureTime)}` : l.serviceTimeStart ? `${formatTime(l.serviceTimeStart)} service` : 'Time to be confirmed';
  return (
    <Link to={`/drive/day/${l.rideAvailabilityID}`} className="rides-option" style={{ textDecoration: 'none', alignItems: 'flex-start' }}>
      <DateTile date={l.date} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ display: 'block', fontSize: 17, lineHeight: 1.3 }}>{l.eventName}</b>
        <span style={{ display: 'block', color: 'var(--rides-muted)', fontSize: 15 }}>{formatShortDate(l.date)}. {time}</span>
        <span style={{ display: 'block', color: 'var(--rides-muted)', fontSize: 15 }}>{l.collectionName} to {l.dropOffName}</span>
        <span style={{ display: 'block', fontSize: 15, marginTop: 4 }}>
          {l.seatsBooked} of {l.seatsTotal} seats booked{riders.length ? `: ${riders.join(', ')}` : ''}
        </span>
        <span style={{ display: 'block', marginTop: 8 }}><StatusPill tone={pill.kind}>{pill.label}</StatusPill></span>
      </span>
      <span className="rides-chev" aria-hidden="true" style={{ alignSelf: 'center' }}><IconChevronRight size={22} /></span>
    </Link>
  );
}
